import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { motion } from 'framer-motion';
import Contact from './Contact';

interface Director {
  name: string;
  title: string;
  qualification: string;
  bio: string;
  image: string | null;
  email: string | null;
  registration?: string;
}

const Accounting: React.FC = () => {
  const directors: Director[] = [
    {
      name: 'Ryan Kim',
      title: 'Partner, Accounting',
      qualification: 'CPA',
      bio: `Ryan is a seasoned accounting professional with 18 years of experience, recognized for delivering proactive, tailored advice to a diverse range of clients. He has held senior leadership roles, including Director of Business Advisory Services and Head of Asia Desk at a top 10 accounting firm. Ryan supports high-net-worth individuals, SMEs and global enterprises with strategic financial and tax planning. His industry expertise spans tech, high-volume e-commerce, medical practices, professional services, retail, wholesale, manufacturing and hospitality. As a CPA, Registered Tax Agent and Chartered Tax Adviser, he combines technical precision with commercial insight. With a strong client-first approach, Ryan consistently delivers customized, growth-focused solutions.`,
      image: '/images/bio/ryan-kim.jpg',
      email: 'ryan.kim@abarcar.com.au',
    },
    {
      name: 'Hanz Wong',
      title: 'Partner, Accounting',
      qualification: 'CPA',
      bio: `Hanz is a CPA Australia-qualified tax accountant with over 18 years of experience working with large corporate clients. He has held roles in both private accounting firms and Big Four firms, including PwC Australia. Hanz specialises in corporate tax, international tax, and cross-border structuring. His expertise includes advising on complex compliance matters, tax governance, and strategic planning. He led teams on major engagements involving multinational clients.`,
      image: '/images/bio/hanz-wong.jpg',
      email: 'hanz.wong@abarcar.com.au',
    },
  ];

  const services = [
    {
      title: 'Comprehensive Tax Services',
      description: `We offer expert tax planning, preparation, and compliance services for individuals, businesses, and trusts. Our team stays up to date with Australian tax legislation and ATO requirements to help minimise your tax liability while ensuring accurate and timely lodgements.`,
    },
    {
      title: 'Business Advisory & Growth Support',
      description: `From start-up to scale-up, we provide strategic business advisory services tailored to your goals. Our support includes budgeting, cash flow forecasting, business structuring, and succession planning to position your business for sustainable success.`,
    },
    {
      title: 'Global Tax & Cross-Border Compliance',
      description: `Expanding overseas or managing international interests? We deliver expert guidance on global tax strategy, cross-border compliance, and international structuring. Whether you're an expat, investor, or multinational, we help you meet complex international obligations with clarity and confidence.`,
    },
    {
      title: 'Cloud Accounting & Software Integration',
      description: `As a Xero Gold Champion Partner, we specialise in cloud-based accounting solutions that streamline your operations and improve financial visibility. We implement and support leading platforms including Xero, MYOB, and QuickBooks, ensuring real-time data access, automation, and seamless system integration to drive efficiency and informed decision-making.`,
    },
  ];

  return (
    <Box>
      {/* Top Banner */}
      <Box
        sx={{
          height: '50vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(/images/generic/gMes5dNykus.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6)',
              }}
            >
              Abarcar Accountants
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Meet Our Directors Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2rem' },
            }}
          >
            Meet Our Directors
          </Typography>
          <Grid container spacing={4}>
            {directors.map((director, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Card
                    sx={{
                      height: { xs: 'auto', md: 800 },
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 3,
                        mb: 2,
                      }}
                    >
                      {director.image && (
                        <Box
                          component="img"
                          src={director.image}
                          alt={director.name}
                          sx={{
                            width: 225,
                            height: 225,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </Box>
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        pt: 0,
                        pb: 3,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {director.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          mb: 1,
                          color: 'secondary.main',
                          fontWeight: 500,
                        }}
                      >
                        {director.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          color: 'text.secondary',
                        }}
                      >
                        {director.qualification === 'CPA' ? 'CPA Australia' : director.qualification}
                        {director.registration && ` (${director.registration})`}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                          // flexGrow: 1,
                          // display: 'flex',
                          alignItems: 'center',
                          maxHeight: '600px',
                        }}
                      >
                        {director.bio}
                      </Typography>
                      {director.email && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 5,
                          color: 'primary.main',
                        }}
                      >
                        {director.email}
                      </Typography>
                    )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Accounting Services Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2rem' },
            }}
          >
            Our Accounting Services
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.7,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Banner */}
      <Box
        sx={{
          height: '40vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(/images/generic/UdYbiywGeg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6)',
              }}
            >
              CONTACT US FOR AN OBLIGATION FREE DISCUSSION
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Get in Touch Section */}
      <Contact />
    </Box>
  );
};

export default Accounting; 